<template>
    <VJoyDialogTrigger :dialog="dialogRef">
        <VJoyTooltip :text="$t('profile.features.share-profile.title')">
            <VJoyIconButton
                id="share-profile-button"
                icon="social-share"
                icon-position="left"
                :label="t('profile.features.share-profile.title')"
                data-testid="freelancer-share-profile-cta-open-dialog"
                size="medium"
                variant="ghost"
                @click="onShareClick"
            />
        </VJoyTooltip>
    </VJoyDialogTrigger>
    <VJoyDialog ref="dialogRef" class="share-profile-dialog" data-testid="freelancer-share-profile-dialog" @dialog:hide="handleSeoDialogHide">
        <template #dialog-header>{{ t('profile.features.share-profile.get.discovered.title') }}</template>
        <template #dialog-body>
            <VJoyWrapper direction="column" align="stretch" row-gap="3" no-margin>
                <VJoyText class="share-profile-dialog__subtitle" preset="primary-400">
                    {{ t('profile.features.share-profile.subtitle') }}
                </VJoyText>
                <!-- Branded visual with overlaid download button -->
                <BrandedVisual />

                <!-- Share via -->
                <div class="share-profile-dialog__actions">
                    <VJoyText preset="primary-450" tag-name="h2" class="share-profile-dialog__section-title">
                        {{ t('profile.features.share-profile.share.profile') }}
                    </VJoyText>
                    <VJoyWrapper :item-stretch="true" :no-margin="true" direction="row" :column-gap="'2'">
                        <ShareProfileSocialNetwork
                            :disabled="isEditingSeoAlias"
                            :profile-link="profileLink"
                            :profile-account-id="profile?.accountId || ''"
                            social-network="linkedin"
                        />
                        <ShareProfileSocialNetwork
                            :disabled="isEditingSeoAlias"
                            :profile-link="profileLink"
                            :profile-account-id="profile?.accountId || ''"
                            social-network="facebook"
                        />
                        <ShareProfileSocialNetwork
                            :disabled="isEditingSeoAlias"
                            :profile-link="profileLink"
                            :profile-account-id="profile?.accountId || ''"
                            social-network="email"
                        />
                    </VJoyWrapper>
                </div>

                <!-- Profile link -->
                <div>
                    <VJoyWrapper :no-margin="true" direction="row" align="center" :column-gap="'1'" class="share-profile-dialog__section-title">
                        <VJoyText preset="primary-450" tag-name="h2">
                            {{ t('profile.features.share-profile.profile.link') }}
                        </VJoyText>
                    </VJoyWrapper>
                    <VJoyWrapper align="flex-end" :no-margin="true" class="share-profile-dialog__seo-alias">
                        <VJoyInput
                            data-testid="freelancer-seo-alias-input"
                            name="freelancer-seo-alias"
                            :model-value="state.seoAlias"
                            :invalid="isEditingSeoAlias && (!!errorSeoAlias || r$.$error)"
                            @focus="handleSeoAliasInputFocus"
                            @update:model-value="handleSeoAliasInputUpdate"
                            @keypress.enter="handleClickOnSave"
                        />
                        <VJoyButton
                            v-if="isEditingSeoAlias"
                            data-testid="freelancer-seo-alias-input-cta-save"
                            :loading="isSavingSeoAlias"
                            @click="handleClickOnSave"
                        >
                            {{ t('generic.form.save.label') }}
                        </VJoyButton>
                        <VJoyButton v-else variant="secondary" data-testid="freelancer-seo-alias-input-cta-copy" @click="handleClickOnCopy">
                            {{ t('generic.action.copy') }}
                        </VJoyButton>
                    </VJoyWrapper>
                    <VJoyFormError v-if="isEditingSeoAlias && errorSeoAlias">
                        {{ errorSeoAlias }}
                    </VJoyFormError>
                    <VJoyFormError v-if="isEditingSeoAlias && r$.$errors.seoAlias?.length > 0">
                        {{ r$.$errors.seoAlias[0] }}
                    </VJoyFormError>
                </div>
            </VJoyWrapper>
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useProfileTracking, useTranslation} from '#imports';
    import {
        VJoyButton,
        VJoyIconButton,
        VJoyDialog,
        VJoyDialogTrigger,
        VJoyFormError,
        VJoyInput,
        VJoyText,
        VJoyWrapper,
        VJoyTooltip,
    } from '@maltjoy/core-vue';
    import {useShareProfile} from '~/features/share-profile/composable/useShareProfile';
    import {useProfileStore} from '~/store/profileStore';
    import BrandedVisual from './BrandedVisual.vue';
    import ShareProfileSocialNetwork from './ShareProfileSocialNetwork.vue';

    const {t} = useTranslation();
    const {profile} = storeToRefs(useProfileStore());
    const {trackProfileSocialShareOpened} = useProfileTracking();

    const {
        errorSeoAlias,
        isEditingSeoAlias,
        isSavingSeoAlias,
        profileLink,
        state,
        r$,
        handleClickOnCopy,
        handleClickOnSave,
        handleSeoAliasInputFocus,
        handleSeoAliasInputUpdate,
        handleSeoDialogHide,
    } = useShareProfile();

    // Refs
    const dialogRef = ref<InstanceType<typeof VJoyDialog>>();

    function onShareClick() {
        trackProfileSocialShareOpened(profile.value?.accountId || '', true);
    }
</script>

<style lang="scss">
    .share-profile-dialog {
        .joy-dialog--header {
            margin-bottom: var(--joy-core-spacing-2) !important;
        }

        .joy-dialog--body {
            max-height: 90vh !important;
            overflow-y: auto;
        }

        &__actions {
            padding-bottom: var(--joy-core-spacing-2) !important;
        }
        &__section-title {
            margin-bottom: var(--joy-core-spacing-2) !important;
        }

        &__subtitle {
            margin-bottom: var(--joy-core-spacing-4) !important;
        }

        &__seo-alias {
            .joy-input--medium {
                flex-grow: 1;
            }
        }
    }
</style>
